import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box, IconButton } from '@mui/material';
import CalculatedValueHelper from '@sharedCR/common/CalculatedValueHelper';
import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import { AutocompleteCDS, DataFieldCDS, DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PopTipCDS from '@sharedCR/coreDesignSystem/PopTipCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { DeleteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import dayjs from 'dayjs';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FieldInputType } from 'constants/fields';
import { MAX_INTEGER_VALUE } from 'constants/number';
import { frequencyOptions, monthsWithZeroOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import {
  checkIsHomeownersInsuranceImpoundType,
  checkIsImpoundCollected,
  getAmountDueDates,
  getCalculatedImpoundFields,
  getMinMaxImpoundDueDate,
  getMonthsToCollect,
} from 'utils/impounds';
import { isFieldValueChanged } from 'utils/loanFees';

import {
  selectApplicationEscrowData,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectIsChattelLoanPurposeFromApplication,
  selectLoanFeesSummary,
  selectTaxesInsuranceEscrowedLoanFeesView,
} from 'store/loanApplications/selectors';

import { LoanFeesTestIds } from '../../../../../../../../LoanFees.testIds';
import {
  deleteButtonStyles,
  dueDateContainerStyles,
  getGridDueDatesContainerStyles,
  gridTopRowContainerStyles,
  impoundContainerStyles,
  rowFormContainerStyles,
} from '../../ImpoundSection.styles';
import {
  checkIsImpoundConversionLocked,
  getHomeownersInsuranceImpoundFields,
} from '../../ImpoundSection.utils';

import {
  HOMEOWNERS_INSURANCE_AMOUNT_DUE_TOOLTIP,
  HOMEOWNERS_INSURANCE_FREQUENCY_TOOLTIP,
} from './ImpoundItem.constants';
import { lockedFieldTooltipAnchorStyles, monthsFieldWrapperStyles } from './ImpoundItem.styles';
import {
  getAnnualAmountError,
  getCalculatedMonthsDifferenceLabel,
  getIsCalculatedMonthsShown,
  getMonthsOption,
  validateAmountDueGroup,
  validateDueDateGroup,
} from './ImpoundItem.utils';

const ImpoundItem = ({
  impound,
  index,
  impoundTypesOptions,
  isLoading,
  monthsCushion = null,
  meta: {
    handleChange,
    validateField,
    setInForm,
    setMultipleInForm,
    validationErrors,
    setValidationErrors,
  },
  onDeleteImpound,
}) => {
  const { firstPaymentDate: prepaidInterestCreditFirstPaymentDate } =
    useSelector(selectLoanFeesSummary);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const taxesInsuranceEscrowed = useSelector(selectTaxesInsuranceEscrowedLoanFeesView);
  const { homeownersInsuranceImpoundId } = useSelector(selectApplicationEscrowData);
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);

  const isDisabledByLock = isLoading || isPageLocked;
  const isMonthsLocked = isDisabledByLock || lockedFieldsMap.monthsCollected;
  const isMonthsFieldDisabled = isMonthsLocked || !taxesInsuranceEscrowed;
  const monthsCollectedValue = impound.monthsCollected?.value ?? null;
  const isCollected = checkIsImpoundCollected(impound, taxesInsuranceEscrowed);
  const dueDateMonths = useMemo(
    () =>
      isChattelLoan
        ? getMonthsToCollect({
            annualAmount: impound.annualAmount,
            amountDueDates: impound.amountDueDates,
            monthsCushion,
            prepaidInterestCreditFirstPaymentDate,
          })
        : null,
    [
      isChattelLoan,
      impound.annualAmount,
      impound.amountDueDates,
      monthsCushion,
      prepaidInterestCreditFirstPaymentDate,
    ],
  );
  const calculatedMonths = dueDateMonths ?? impound.calculatedMonthsCollected;
  const isFollowingCalculatedMonths = !impound.monthsCollectedOverridden;
  const isCalculatedMonthsShown = getIsCalculatedMonthsShown({
    isEscrowed: taxesInsuranceEscrowed,
    isOverridden: impound.monthsCollectedOverridden,
    monthsCollected: monthsCollectedValue,
    calculatedMonths,
  });
  const calculatedMonthsDifferenceLabel = getCalculatedMonthsDifferenceLabel({
    monthsCollected: monthsCollectedValue,
    calculatedMonths,
  });
  const isHomeownersInsurance = checkIsHomeownersInsuranceImpoundType(
    impound.impoundTypeId,
    homeownersInsuranceImpoundId,
  );
  const isImpoundConversionLocked = checkIsImpoundConversionLocked({
    isPageLocked,
    lockedFieldsMap,
  });
  const shouldApplyHomeownersInsuranceLock = isHomeownersInsurance && !isImpoundConversionLocked;

  const { minDate, maxDate } = getMinMaxImpoundDueDate({ prepaidInterestCreditFirstPaymentDate });

  const impoundFormName = `set_impounds[${index}]`;
  const frequencyNumber = impound.frequency?.secondaryValue || 0;
  const annualAmountError = getAnnualAmountError({
    validationErrors,
    impoundFormName,
    shouldApplyHomeownersInsuranceLock,
  });

  useEffect(() => {
    const { monthlyAmount, collectedAmount } = getCalculatedImpoundFields({
      monthsCollected: impound.monthsCollected?.value || 0,
      annualAmount: impound.annualAmount || 0,
    });

    setMultipleInForm({
      [`set_impounds[${index}].monthlyAmount`]: monthlyAmount,
      [`set_impounds[${index}].collectedAmount`]: isCollected ? collectedAmount : 0,
    });
  }, [
    setMultipleInForm,
    index,
    isCollected,
    impound.annualAmount,
    impound.monthsCollected?.value,
    impound.frequency?.value,
  ]);

  useEffect(() => {
    if (
      !isFollowingCalculatedMonths ||
      isMonthsFieldDisabled ||
      _.isNil(dueDateMonths) ||
      dueDateMonths === monthsCollectedValue
    ) {
      return;
    }

    const fieldName = `${impoundFormName}.monthsCollected`;
    const value = getMonthsOption(dueDateMonths);

    setInForm(fieldName, value);
    validateField(fieldName, value);
  }, [
    isFollowingCalculatedMonths,
    isMonthsFieldDisabled,
    dueDateMonths,
    monthsCollectedValue,
    impoundFormName,
    setInForm,
    validateField,
  ]);

  useEffect(() => {
    setTimeout(() => validateAmountDueGroup({ index, impound, setValidationErrors }), 0);
  }, [index, impound, setValidationErrors]);

  useEffect(() => {
    setTimeout(
      () =>
        validateDueDateGroup({
          impound,
          impoundFormName,
          prepaidInterestCreditFirstPaymentDate,
          setValidationErrors,
        }),
      1,
    );
  }, [impound, impoundFormName, prepaidInterestCreditFirstPaymentDate, setValidationErrors]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onChangeNumberField = useCallback(
    ({ target }) => {
      handleChange({ target: { ...target, value: Number(target.value) } });
    },
    [handleChange],
  );

  const onImpoundTypeChange = useCallback(
    (e, option) => {
      onAutocompleteChange(e, option);

      if (
        !checkIsHomeownersInsuranceImpoundType(option, homeownersInsuranceImpoundId) ||
        isImpoundConversionLocked
      ) {
        return;
      }

      const { frequency, amountDueDates } = getHomeownersInsuranceImpoundFields({
        amountDueDates: impound.amountDueDates,
        annualAmount: impound.annualAmount,
      });

      const frequencyPath = `${impoundFormName}.frequency`;

      setMultipleInForm({
        [frequencyPath]: frequency,
        [`${impoundFormName}.amountDueDates`]: amountDueDates,
      });
      validateField(frequencyPath, frequency);
    },
    [
      onAutocompleteChange,
      homeownersInsuranceImpoundId,
      isImpoundConversionLocked,
      impound.amountDueDates,
      impound.annualAmount,
      setMultipleInForm,
      impoundFormName,
      validateField,
    ],
  );

  const onAnnualAmountChange = useCallback(
    e => {
      onChangeNumberField(e);

      if (shouldApplyHomeownersInsuranceLock) {
        const amountDuePath = `${impoundFormName}.amountDueDates[0].amountDue`;
        const amountDue = Number(e.target.value);

        setInForm(amountDuePath, amountDue);
        validateField(amountDuePath, amountDue);
      }
    },
    [
      onChangeNumberField,
      shouldApplyHomeownersInsuranceLock,
      impoundFormName,
      setInForm,
      validateField,
    ],
  );

  const handleDateChange = useCallback(
    ({ name }) =>
      value => {
        const date = dayjs(value).isValid() ? value : null;

        setInForm(name, date);
        validateField(name, date);
      },
    [setInForm, validateField],
  );

  const onFrequencyChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (impound.annualAmount) {
        const formState = { annualAmount: impound.annualAmount, frequency: option };
        const amountDueDates = getAmountDueDates({
          formState,
          firstPaymentDate: prepaidInterestCreditFirstPaymentDate,
          isResultArray: true,
        });
        setInForm(`set_impounds[${index}].amountDueDates`, amountDueDates, true);
      }
    },
    [setInForm, validateField, index, impound.annualAmount, prepaidInterestCreditFirstPaymentDate],
  );

  const setMonthsCollectedOverridden = useCallback(
    isOverridden => setInForm(`${impoundFormName}.monthsCollectedOverridden`, isOverridden),
    [impoundFormName, setInForm],
  );

  const onMonthsChange = useCallback(
    (e, option) => {
      onAutocompleteChange(e, option);
      setMonthsCollectedOverridden(option?.value !== calculatedMonths);
    },
    [onAutocompleteChange, setMonthsCollectedOverridden, calculatedMonths],
  );

  const onRevertMonthsToCalculated = useCallback(() => {
    const fieldName = `${impoundFormName}.monthsCollected`;
    const value = getMonthsOption(calculatedMonths);

    setInForm(fieldName, value);
    validateField(fieldName, value);
    setMonthsCollectedOverridden(false);
  }, [impoundFormName, calculatedMonths, setInForm, validateField, setMonthsCollectedOverridden]);

  const monthsInputProps = useMemo(
    () => ({
      customHelper: isCalculatedMonthsShown ? (
        <CalculatedValueHelper
          calculatedValue={calculatedMonths}
          differenceLabel={calculatedMonthsDifferenceLabel}
          isRevertHidden={isMonthsLocked}
          revertButtonTestId={LoanFeesTestIds.getImpoundMonthsRevertButtonId(index)}
          onRevert={onRevertMonthsToCalculated}
        />
      ) : null,
    }),
    [
      isCalculatedMonthsShown,
      calculatedMonths,
      calculatedMonthsDifferenceLabel,
      isMonthsLocked,
      index,
      onRevertMonthsToCalculated,
    ],
  );

  if (impound.pendingChangesActionType === PendingChangesActionType.DELETED) {
    return null;
  }

  return (
    <Box
      className={`table-row-pending-${impound.pendingChangesActionType}`}
      sx={impoundContainerStyles}
    >
      <Box sx={rowFormContainerStyles}>
        <Box sx={gridTopRowContainerStyles}>
          <AutocompleteCDS
            disableClearable
            fullWidth
            required
            withPopupError
            dataTestId={LoanFeesTestIds.getImpoundTypeSelectId(index)}
            disabled={isDisabledByLock || lockedFieldsMap.impoundTypeId}
            error={!!_.get(validationErrors, `${impoundFormName}.impoundTypeId`)}
            helperText={_.get(validationErrors, `${impoundFormName}.impoundTypeId`)}
            id={`${impoundFormName}.impoundTypeId`}
            isFieldChanged={isFieldValueChanged(impound, 'impoundTypeId')}
            label="Impound Type"
            name={`${impoundFormName}.impoundTypeId`}
            options={impoundTypesOptions}
            placeholder="Select one"
            size="small"
            value={impound.impoundTypeId}
            onChange={onImpoundTypeChange}
          />

          <PopTipCDS
            describeChild
            placement="top"
            title={shouldApplyHomeownersInsuranceLock ? HOMEOWNERS_INSURANCE_FREQUENCY_TOOLTIP : ''}
          >
            <Box sx={lockedFieldTooltipAnchorStyles}>
              <AutocompleteCDS
                disableClearable
                fullWidth
                required
                withPopupError
                dataTestId={LoanFeesTestIds.getImpoundFrequencySelectId(index)}
                disabled={
                  isDisabledByLock ||
                  lockedFieldsMap.frequency ||
                  shouldApplyHomeownersInsuranceLock
                }
                error={!!_.get(validationErrors, `${impoundFormName}.frequency`)}
                helperText={_.get(validationErrors, `${impoundFormName}.frequency`)}
                id={`${impoundFormName}.frequency`}
                isFieldChanged={isFieldValueChanged(impound, 'frequency')}
                label="Frequency"
                name={`${impoundFormName}.frequency`}
                options={frequencyOptions}
                placeholder="Select one"
                size="small"
                value={impound.frequency}
                withSortedOptions={false}
                onChange={onFrequencyChange}
              />
            </Box>
          </PopTipCDS>

          <DataFieldCDS
            required
            withPopupError
            allowNegative={false}
            data-is-amount="true"
            dataTestId={LoanFeesTestIds.getImpoundAnnualAmountInputId(index)}
            disabled={isDisabledByLock || lockedFieldsMap.annualAmount}
            error={!!annualAmountError}
            helperText={annualAmountError}
            id={`${impoundFormName}.annualAmount`}
            isFieldChanged={isFieldValueChanged(impound, 'annualAmount')}
            label="Annual Amount"
            maxValue={MAX_INTEGER_VALUE}
            minValue={0}
            name={`${impoundFormName}.annualAmount`}
            placeholder="Please enter"
            size="small"
            type={FieldInputType.CURRENCY}
            value={impound.annualAmount}
            onChange={onAnnualAmountChange}
          />

          <DataFieldCDS
            disabled
            required
            withPopupError
            allowNegative={false}
            decimalScale={2}
            decimalSeparator="."
            error={!!_.get(validationErrors, `${impoundFormName}.monthlyAmount`)}
            helperText={_.get(validationErrors, `${impoundFormName}.monthlyAmount`)}
            id={`${impoundFormName}.monthlyAmount`}
            label="Monthly Amount"
            maxValue={MAX_INTEGER_VALUE}
            name={`${impoundFormName}.monthlyAmount`}
            placeholder="Please enter"
            size="small"
            type={FieldInputType.CURRENCY}
            value={impound.monthlyAmount}
          />

          <Box sx={monthsFieldWrapperStyles}>
            <AutocompleteCDS
              disableClearable
              fullWidth
              required
              withPopupError
              dataTestId={LoanFeesTestIds.getImpoundMonthsSelectId(index)}
              disabled={isMonthsFieldDisabled}
              error={!!_.get(validationErrors, `${impoundFormName}.monthsCollected`)}
              helperText={_.get(validationErrors, `${impoundFormName}.monthsCollected`)}
              id={`${impoundFormName}.monthsCollected`}
              inputProps={monthsInputProps}
              isFieldChanged={isFieldValueChanged(impound, 'monthsCollected')}
              label="Months"
              name={`${impoundFormName}.monthsCollected`}
              options={monthsWithZeroOptions}
              placeholder="Select one"
              size="small"
              value={impound.monthsCollected}
              onChange={onMonthsChange}
            />
          </Box>

          <DataFieldCDS
            disabled
            required
            withPopupError
            allowNegative={false}
            decimalScale={2}
            decimalSeparator="."
            error={!!_.get(validationErrors, `${impoundFormName}.collectedAmount`)}
            helperText={_.get(validationErrors, `${impoundFormName}.collectedAmount`)}
            id={`${impoundFormName}.collectedAmount`}
            label="Collected Amount"
            maxValue={MAX_INTEGER_VALUE}
            name={`${impoundFormName}.collectedAmount`}
            placeholder="Please enter"
            size="small"
            type={FieldInputType.CURRENCY}
            value={impound.collectedAmount}
          />
        </Box>

        {!!frequencyNumber && (
          <Box sx={getGridDueDatesContainerStyles({ frequencyNumber })}>
            {_.range(frequencyNumber).map((__, dueDatesIndex) => {
              const dueDatesFormName = `${impoundFormName}.amountDueDates[${dueDatesIndex}]`;
              const { dueDate = null, amountDue = null } =
                impound.amountDueDates?.[dueDatesIndex] || {};

              return (
                <Box
                  key={`${impound.applicationImpoundId}-${dueDatesIndex}`}
                  sx={dueDateContainerStyles}
                >
                  <DateFieldCDS
                    disableClearable
                    fullWidth
                    required
                    withPopupError
                    data-testid={LoanFeesTestIds.getDueDateId(dueDatesFormName)}
                    disabled={isDisabledByLock || lockedFieldsMap.dueDate}
                    error={!!_.get(validationErrors, `${dueDatesFormName}.dueDate`)}
                    helperText={_.get(validationErrors, `${dueDatesFormName}.dueDate`)}
                    id={`${dueDatesFormName}.dueDate`}
                    isFieldChanged={isFieldValueChanged(
                      impound,
                      `amountDueDates[${dueDatesIndex}].dueDate`,
                    )}
                    label={`Due Date ${dueDatesIndex + 1}`}
                    maxDate={maxDate}
                    minDate={minDate}
                    name={`${dueDatesFormName}.dueDate`}
                    size="small"
                    value={dueDate}
                    onBlur={_.noop}
                    onChange={handleDateChange({ name: `${dueDatesFormName}.dueDate` })}
                    onFocus={_.noop}
                  />

                  <PopTipCDS
                    describeChild
                    placement="top"
                    title={
                      shouldApplyHomeownersInsuranceLock
                        ? HOMEOWNERS_INSURANCE_AMOUNT_DUE_TOOLTIP
                        : ''
                    }
                  >
                    <Box sx={lockedFieldTooltipAnchorStyles}>
                      <DataFieldCDS
                        required
                        withPopupError
                        allowNegative={false}
                        dataTestId={LoanFeesTestIds.getImpoundAmountDueInputId(
                          index,
                          dueDatesIndex,
                        )}
                        disabled={
                          isDisabledByLock ||
                          lockedFieldsMap.amountDue ||
                          shouldApplyHomeownersInsuranceLock
                        }
                        error={!!_.get(validationErrors, `${dueDatesFormName}.amountDue`)}
                        helperText={_.get(validationErrors, `${dueDatesFormName}.amountDue`)}
                        id={`${dueDatesFormName}.amountDue`}
                        isFieldChanged={isFieldValueChanged(
                          impound,
                          `amountDueDates[${dueDatesIndex}].amountDue`,
                        )}
                        label={`Amount Due ${dueDatesIndex + 1}`}
                        maxValue={MAX_INTEGER_VALUE}
                        name={`${dueDatesFormName}.amountDue`}
                        placeholder="Please enter"
                        size="small"
                        type={FieldInputType.CURRENCY}
                        value={amountDue}
                        onChange={onChangeNumberField}
                      />
                    </Box>
                  </PopTipCDS>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <IconButton
        disableRipple
        disableTouchRipple
        className="label-button"
        data-impound-id={impound.applicationImpoundId}
        data-testid={LoanFeesTestIds.getImpoundDeleteButtonId(index)}
        disabled={isLoading}
        sx={deleteButtonStyles}
        onClick={onDeleteImpound}
      >
        <DeleteIcon size={IconSize[20]} />
      </IconButton>
    </Box>
  );
};

ImpoundItem.propTypes = {
  ...WithLoadingPropTypes,
  impound: PropTypes.shape({
    applicationImpoundId: PropTypes.string,
    impoundTypeId: PropTypes.object,
    impoundTypeName: PropTypes.string,
    frequency: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
      secondaryValue: PropTypes.number,
    }),
    annualAmount: PropTypes.number,
    monthlyAmount: PropTypes.number,
    collectedAmount: PropTypes.number,
    monthsCollected: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
    }),
    calculatedMonthsCollected: PropTypes.number,
    monthsCollectedOverridden: PropTypes.bool,
    amountDueDates: PropTypes.arrayOf(
      PropTypes.shape({
        dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        amountDue: PropTypes.number,
      }),
    ),
    paidThroughDate: PropTypes.object,
    lastPaymentAmount: PropTypes.number,
    isLocallyAdded: PropTypes.bool,
    isDeleted: PropTypes.bool,
    pendingChangesActionType: PropTypes.string,
    initialFee: PropTypes.object,
  }).isRequired,
  index: PropTypes.number.isRequired,
  impoundTypesOptions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      category: PropTypes.string,
    }),
  ).isRequired,
  meta: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
    setInForm: PropTypes.func.isRequired,
    setMultipleInForm: PropTypes.func.isRequired,
    setForm: PropTypes.func.isRequired,
    setIsTouched: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    setValidationErrors: PropTypes.func.isRequired,
    setEditedSection: PropTypes.func.isRequired,
  }).isRequired,
  onDeleteImpound: PropTypes.func.isRequired,
  monthsCushion: PropTypes.number,
};

export default ImpoundItem;
